import { useContext } from "react";
import { Card } from "react-bootstrap";
import { useFreshnessNow } from "@/common/hooks/useFreshnessNow";
import FreshnessChip from "@/components/analytics/FreshnessChip";
import { MetricSourceContext, type MetricSource } from "@/components/analytics/metricSource";
import SourceInfoPopover from "@/components/analytics/SourceInfoPopover";
import { buildAsOfFooter, selectFreshness } from "@/lib/analytics/freshness";

type TileSourceLineProps = {
  /** The tile's name, for the source popover's accessible name. */
  label: string;
  source: MetricSource;
  /** The footer text from `buildAsOfFooter`. */
  asOf: string;
  /** Epoch ms to judge the chip against; the page's shared clock by default. */
  now?: number;
};

/**
 * A tile's as-of time (AC4), freshness chip (AC3) and source popover (AC2) on one line. The chip
 * ages on the page's shared clock, so an open page moves from Fresh to Delayed to Stale.
 */
export const TileSourceLine = ({ label, source, asOf, now }: TileSourceLineProps) => {
  const ticking = useFreshnessNow();
  const chip = source.meta ? selectFreshness(source.meta, now ?? ticking) : null;
  return (
    <div className="pz-stat__footer">
      {/* A Live chip already prints the footer's words. */}
      {chip?.state === "live" ? null : <span>{asOf}</span>}
      {chip ? <FreshnessChip chip={chip} /> : null}
      <SourceInfoPopover label={label} kind={source.kind} meta={source.meta} />
    </div>
  );
};

/**
 * The source line as the footer of a plain card inside a tile frame (report ch.6: "a freshness chip
 * on each card"). Outside a frame it renders nothing, so the card renders as before; a response with
 * no as-of time never reaches here, because the frame shows Error instead of the card.
 */
export const TileSourceFooter = ({ label }: { label: string }) => {
  const source = useContext(MetricSourceContext);
  if (!source) return null;
  const footer = buildAsOfFooter([source.meta]);
  if (!footer.ok) return null;
  return (
    <Card.Footer className="bg-transparent">
      <TileSourceLine label={label} source={source} asOf={footer.text} />
    </Card.Footer>
  );
};
